import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { createPortal } from "react-dom";
import { useJourney } from "@/context/JourneyContext";
import { StageFrame, Eyebrow, EASE } from "@/components/StageFrame";
import { ConsultantNav } from "@/components/Navigation";
import { Users, BedDouble, Bath, Sofa, Heart, Sparkles, Layers, Building2, CheckCircle2, Maximize2, X } from "lucide-react";

const FLOORPLAN_3D = "https://static.prod-images.emergentagent.com/jobs/9328c3b4-4557-4408-9bb0-88391ddd609d/images/b5db5d9f3f2c634b6c5a761225819bcaa4eac96a33db74d5086187b10c03507c.jpeg";

function Tile({ children, className = "", delay = 0, testId }) {
  return (
    <motion.div
      data-testid={testId}
      initial={{ opacity: 0, y: 22 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7, ease: EASE, delay }}
      className={`rounded-2xl border border-line bg-white/5 p-6 ${className}`}
    >
      {children}
    </motion.div>
  );
}

function Chips({ items }) {
  return (
    <div className="flex flex-wrap gap-2 mt-3">
      {(items || []).map((x, i) => (
        <span key={i} className="rounded-full bg-surface border border-line px-3 py-1 text-xs text-forest/80">{x}</span>
      ))}
    </div>
  );
}

export default function LifeProfile() {
  const { data, next, back } = useJourney();
  const p = data.life_profile || {};
  const [expanded, setExpanded] = useState(false);

  return (
    <StageFrame testId="stage-life-profile" className="px-6 md:px-14 pt-20 pb-32">
      <div className="max-w-6xl mx-auto">
        <Eyebrow>Your Life Profile</Eyebrow>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-10 items-center mt-3">
          <div>
            <h1 className="font-serif text-4xl md:text-6xl font-light leading-[1.0] tracking-tight text-forest max-w-2xl">
              {p.lifestyle_statement || "This is how you want to live."}
            </h1>
            <p className="text-mutedText mt-4 max-w-xl">{p.household_summary}</p>
          </div>
          <motion.figure
            data-testid="profile-floorplan"
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, ease: EASE }}
            className="relative overflow-hidden rounded-2xl border border-line bg-surface"
          >
            <motion.div
              animate={{ y: [0, -8, 0] }}
              transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
              className="relative"
            >
              <img src={FLOORPLAN_3D} alt="Playful single-storey 3D floor plan where a kid has set the kitchen on fire" className="w-full object-cover" />

              {/* animated light sweep */}
              <motion.div
                aria-hidden
                initial={{ x: "-120%" }}
                animate={{ x: "120%" }}
                transition={{ duration: 4.5, repeat: Infinity, repeatDelay: 2.5, ease: "easeInOut" }}
                className="pointer-events-none absolute inset-y-0 -left-1/3 w-1/3 skew-x-12 bg-gradient-to-r from-transparent via-white/15 to-transparent"
              />

              {/* expand control */}
              <button
                data-testid="profile-floorplan-expand"
                onClick={() => setExpanded(true)}
                className="absolute right-3 top-3 flex items-center gap-1.5 rounded-full bg-black/60 px-3 py-1.5 text-xs font-medium text-white backdrop-blur-sm transition hover:bg-black/80"
                title="Expand"
              >
                <Maximize2 size={13} /> Expand
              </button>
            </motion.div>

            <figcaption className="px-4 py-3 text-xs italic text-mutedText">
              Single-storey, room to breathe… and a "coffee command centre" the kids will absolutely never set on fire. 🔥😄
            </figcaption>
          </motion.figure>

          {/* lightbox */}
          {createPortal(
            <AnimatePresence>
            {expanded && (
              <motion.div
                data-testid="profile-floorplan-lightbox"
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                onClick={() => setExpanded(false)}
                className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4 backdrop-blur-sm"
              >
                <button
                  data-testid="profile-floorplan-close"
                  onClick={() => setExpanded(false)}
                  className="absolute right-5 top-5 grid h-10 w-10 place-items-center rounded-full bg-white/10 text-white transition hover:bg-white/20"
                  title="Close"
                >
                  <X size={20} />
                </button>
                <motion.img
                  initial={{ scale: 0.92 }} animate={{ scale: 1 }} exit={{ scale: 0.92 }}
                  transition={{ ease: EASE, duration: 0.35 }}
                  src={FLOORPLAN_3D}
                  alt="Expanded single-storey 3D floor plan"
                  onClick={(e) => e.stopPropagation()}
                  className="max-h-[92vh] max-w-[92vw] rounded-2xl object-contain shadow-2xl"
                />
              </motion.div>
            )}
            </AnimatePresence>,
            document.body
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 mt-10">
          <Tile testId="profile-household" className="md:col-span-5" delay={0.05}>
            <div className="flex items-center gap-2 text-brand"><Users size={18} /><span className="label-eyebrow text-forest/70">Household</span></div>
            <Chips items={p.household_members} />
          </Tile>

          <Tile testId="profile-rooms" className="md:col-span-4" delay={0.1}>
            <div className="flex items-center gap-2 text-brand mb-3"><BedDouble size={18} /><span className="label-eyebrow text-forest/70">Home shape</span></div>
            <div className="flex items-center gap-5 text-forest">
              <span className="flex items-center gap-1.5 font-serif text-3xl">{p.bedrooms_needed ?? "—"}<BedDouble size={16} className="text-mutedText" /></span>
              <span className="flex items-center gap-1.5 font-serif text-3xl">{p.bathrooms_needed ?? "—"}<Bath size={16} className="text-mutedText" /></span>
              <span className="flex items-center gap-1.5 font-serif text-3xl">{p.living_areas_needed ?? "—"}<Sofa size={16} className="text-mutedText" /></span>
            </div>
          </Tile>

          <Tile testId="profile-storey" className="md:col-span-3" delay={0.15}>
            <div className="flex items-center gap-2 text-brand mb-2"><Layers size={18} /><span className="label-eyebrow text-forest/70">Size & storey</span></div>
            <p className="text-forest capitalize">{p.storeys_preference || "any"} storey</p>
            <p className="text-mutedText text-sm capitalize">{p.size_preference || "medium"} footprint</p>
          </Tile>

          <Tile testId="profile-musthave" className="md:col-span-4" delay={0.2}>
            <div className="flex items-center gap-2 text-brand"><Sparkles size={18} /><span className="label-eyebrow text-forest/70">Essential rooms</span></div>
            <Chips items={p.must_have_rooms} />
          </Tile>

          <Tile testId="profile-priorities" className="md:col-span-4" delay={0.25}>
            <div className="flex items-center gap-2 text-brand"><Heart size={18} /><span className="label-eyebrow text-forest/70">Priorities</span></div>
            <Chips items={p.priorities} />
          </Tile>

          <Tile testId="profile-future" className="md:col-span-4" delay={0.3}>
            <div className="flex items-center gap-2 text-brand"><Building2 size={18} /><span className="label-eyebrow text-forest/70">Future needs</span></div>
            <Chips items={p.future_needs} />
            {p.multigenerational && <p className="mt-3 text-xs text-brand font-medium">Multigenerational living</p>}
          </Tile>

          {(p.room_relationships || []).length > 0 && (
            <Tile testId="profile-relationships" className="md:col-span-12 bg-white/5" delay={0.35}>
              <span className="label-eyebrow text-forest/70">How the rooms relate</span>
              <Chips items={p.room_relationships} />
            </Tile>
          )}
        </div>

        <div className="mt-10 flex items-center gap-2 text-forest/80">
          <CheckCircle2 size={18} className="text-brand" />
          <span className="text-sm">Read this back together, then confirm.</span>
        </div>
      </div>

      <ConsultantNav testId="stage-life-profile" onBack={back} onNext={next} nextLabel="Yes, this is how we want to live" />
    </StageFrame>
  );
}
